/**
 * Returns a callback that guarantees the canvas agent floating chat is
 * open: it re-activates the most-recent existing main session, or mints
 * a fresh `CANVAS_AGENT` session if none exist. A no-op when a chat is
 * already open.
 *
 * Used by canvas window-event handlers (`useCanvasWindowEvents`) that
 * need to surface the chat without each call site re-implementing the
 * session-resolution logic.
 */

import { useCreateFlowSession } from "@/api/flow_sessions";
import Sentry from "@/integrations/Sentry";
import { FLOW_SESSION_MODES, type FlowSession } from "@cohesive/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import toast from "react-hot-toast";
import { useCanvasFloatingChat } from "../context/CanvasFloatingChatContext";
import { useCanvasRoute } from "../context/CanvasRouteContext";

export function useEnsureCanvasAgentChatOpen(): () => Promise<void> {
  const { canvasId } = useCanvasRoute();
  const queryClient = useQueryClient();
  const { openSession: openCanvasFloatingSession, currentSessionId } =
    useCanvasFloatingChat();
  const createFlowSession = useCreateFlowSession();

  return useCallback(async () => {
    if (!canvasId) return;
    if (currentSessionId) return;

    // Prefer reactivating the most-recently-updated existing main
    // session (action-item sessions are excluded — they're scoped to a
    // specific node, not the canvas-wide agent chat).
    const sessions =
      queryClient.getQueryData<FlowSession[]>([
        "canvas-flow-sessions",
        canvasId,
      ]) ?? [];
    const mainSessions = sessions.filter((s) => !s.action_item_id);
    const sorted = [...mainSessions].sort(
      (a, b) =>
        new Date(b.updated_at ?? 0).getTime() -
        new Date(a.updated_at ?? 0).getTime(),
    );
    if (sorted.length > 0) {
      openCanvasFloatingSession(sorted[0]!.id);
      return;
    }

    // No existing session — mint a fresh canvas-agent one.
    try {
      const newSession = await createFlowSession.mutateAsync({
        mode: FLOW_SESSION_MODES.CANVAS_CHAT,
        class: "canvas",
        canvas_id: canvasId,
      });
      await queryClient.invalidateQueries({
        queryKey: ["canvas-flow-sessions", canvasId],
      });
      openCanvasFloatingSession(newSession.id);
    } catch (err) {
      Sentry.captureException(err);
      toast.error("Failed to open chat");
    }
  }, [
    canvasId,
    currentSessionId,
    queryClient,
    openCanvasFloatingSession,
    createFlowSession,
  ]);
}
